import _ from "lodash";
import { ClassificationRecordsSearchRequest } from "../models/ClassificationRecordsSearchRequest";
import { ClassificationRecordsTreeSearchRequest } from "../models/ClassificationRecordsTreeSearchRequest";
import { ClassificationSearchRequest } from "../models/ClassificationSearchRequest";
import { ClassifierBaseDataRequest } from "../models/ClassifierBaseDataRequest";
import { ClassifierRecordDetailsRequest } from "../models/ClassifierRecordDetailsRequest";
import { TreeLayerRecordsRequest } from "../models/TreeLayerRecordsRequest";
import { GlobalSearchRequest } from "../models/GlobalSearchRequest";
import { RecordHistoryRequest } from "../models/RecordHistoryRequest";
import queryString from "query-string";
import { StaticPageRequest } from "../models/StaticPageRequest";
import { TopClassifiersRequest } from "../models/TopClassifiersRequest";
import { SystemConfigGroupParametersRequest } from "../models/SystemConfigGroupParametersRequest";
import { LodResourceDataRequest } from "../models/LodResourceDataRequest";
import { LodResourceTemplateRequest } from "../models/LodResourceTemplateRequest";
import { LodResourceFileRequest } from "../models/LodResourceFileRequest";
import { DownloadClassifierRecordsRequest } from "../models/DownloadClassifierRecordsRequest";
import {LodResourceListRequest} from "../models/LodResourceListRequest";
import {ClassifierStructureExistenceInfoRequest} from "../models/ClassifierStructureExistenceInfoRequest";
import {ClassifierRecordLevelRequest} from "../models/ClassifierRecordLevelRequest";

let apiUrl = process.env.REACT_APP_API_URL;

function api(route: string) {
    return `${apiUrl}/${route}`;
}

function buildQuery(queryObject: any): string {
    let urlSearchParams = new URLSearchParams(
        queryString.stringify(queryObject)
    );

    let property: keyof typeof queryObject;
    for (property in queryObject) {
        if (typeof queryObject[property] === "undefined") continue;

        if (_.isDate(queryObject[property])) {
            const dateValue = queryObject[property] as Date;
            urlSearchParams.set(property, dateValue.toISOString());
        }
    }

    return urlSearchParams.toString();
}

class Search {
    public GetGlobalResults = (request: GlobalSearchRequest) =>
        api(`api/search/global?${buildQuery(request)}`);
    public GetClassificationResults = (request: ClassificationSearchRequest) =>
        api(`api/search/classification?${buildQuery(request)}`);
    public GetClassificationRecordResults = (
        request: ClassificationRecordsSearchRequest
    ) => {
        return api(`api/search/classification-records?${buildQuery(request)}`);
    };
    public GetClassificationTreeRecordResults = (
        request: ClassificationRecordsTreeSearchRequest
    ) => {
        return api(
            `api/search/classification-tree-records?${buildQuery(request)}`
        );
    };
    public EvaluateSuggestedRecord = () => api(`api/search/evaluate-record`);
    public EvaluateRecordDownload = () => api(`api/search/evaluate-download`);
}

class Classifiers {
    public Top = (request: TopClassifiersRequest) =>
        api(`api/classifiers/top/${request.localeCode}`);
    public Get = (request: ClassifierBaseDataRequest) =>
        api(`api/classifiers/${request.id}/${request.localeCode}`);
    public GetRecordsTreeLayer = (request: TreeLayerRecordsRequest) => {
        return api(
            `api/classifiers/${
                request.classifierId
            }/records/tree-layer?${buildQuery(request)}`
        );
    };
    public GetRecordDetails = (request: ClassifierRecordDetailsRequest) => {
        return api(
            `api/classifiers/${request.classifierId}/${encodeURIComponent(
                request.localeCode
            )}/records/${request.recordId}`
        );
    };
    public GetRecordHistory = (request: RecordHistoryRequest) => {
        return api(
            `api/classifiers/${request.classifierId}/records` +
                `/${encodeURIComponent(request.code)}` +
                `/${encodeURIComponent(request.localeCode)}` +
                `/${encodeURIComponent(request.recordLocaleCode)}`
        );
    };
    public DownloadRecords = (request: DownloadClassifierRecordsRequest) => {
        return api(
            `api/classifiers/${request.id}/records` +
                `/${encodeURIComponent(request.localeCode)}/download` +
                `/${request.fileType}?${buildQuery(request)}`
        );
    };
    public GetFile = (id: number) => api(`api/classifiers/files/${id}`);
    public GetClassifierStructuresExistenceInfo = (request: ClassifierStructureExistenceInfoRequest) =>
        api(`api/classifiers/records/existence?${buildQuery(request)}`);
    public GetClassifierRecordsLevels = (request: ClassifierRecordLevelRequest) => {
        return api(`api/classifiers/record/levels?${buildQuery(request)}`);
    }
}

class StaticPages {
    public Get = (request: StaticPageRequest) =>
        api(
            `api/static-pages/${encodeURIComponent(
                request.localeCode
            )}/${encodeURIComponent(request.path)}`
        );
    public GetFile = (id: number) => api(`api/static-pages/files/${id}`);
}

class Localization {
    public I18n = (languageCode: string) =>
        api(`api/localization/i18n/${languageCode}`);
    public Locales = () => api(`api/localization/locales`);
}

class SystemConfig {
    public GetParameters = (request: SystemConfigGroupParametersRequest) =>
        api(
            `api/system-config/${request.type}/${encodeURIComponent(
                request.localeCode
            )}`
        );
}

class Lod {
    public ResourceContent = (request: LodResourceDataRequest) =>
        api(
            `api/lod/resources/${encodeURIComponent(
                request.klasisCode
            )}/${encodeURIComponent(
                request.localeCode
            )}/content?subject=${encodeURIComponent(request.subject ?? "")}`
        );
    public ResourceFile = (request: LodResourceFileRequest) =>
        api(
            `api/lod/resources` +
                `/${encodeURIComponent(request.klasisCode)}` +
                `/${encodeURIComponent(request.localeCode)}` +
                `/download/` +
                `${request.fileType}?subject=${encodeURIComponent(
                    request.subject ?? ""
                )}`
        );
    public Resources = (request: LodResourceListRequest) =>
        api(`api/lod/resources/${encodeURIComponent(request.localeCode)}`);
    public Templates = (request: LodResourceTemplateRequest) =>
        api(
            `api/lod/resources/templates/${encodeURIComponent(
                request.localeCode
            )}`
        );
}

interface IApiRoutes {
    Search: Search;
    Classifiers: Classifiers;
    Localization: Localization;
    StaticPages: StaticPages;
    SystemConfig: SystemConfig;
    Lod: Lod;
}

export const ApiRoutes: IApiRoutes = {
    Search: new Search(),
    Classifiers: new Classifiers(),
    Localization: new Localization(),
    StaticPages: new StaticPages(),
    SystemConfig: new SystemConfig(),
    Lod: new Lod(),
};
